import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Search, Eye, Check, X, MessageSquare, FileCheck2, Clock, User, Calendar,
  UserCheck, Loader2, CheckCheck, Paperclip, FileText, Image as ImageIcon,
} from "lucide-react";
import {
  requestsApi,
  CorrectionRequest,
  CorrectionRequestType,
  CorrectionRequestStatus,
  RequestAttachment,
  ListRequestsQuery,
  RequestSummary,
} from "@/lib/requests-api";
import { ApiError, fetchSignedAsset } from "@/lib/api";
import { formatDateIn, formatDateTimeIn } from "@/lib/datetime";
import { RequestMetadataPanel, hasStructuredDetail } from "@/components/RequestMetadataPanel";
import { useTenantTimezone } from "@/hooks/useTenantTimezone";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import { usePage } from "@/hooks/usePage";
import { ListPager } from "@/components/ListPager";
import { displayTypeName } from "@/lib/request-labels";

/**
 * Requests per page. The page used to fetch one page of 200 and treat it as
 * everything: older requests were unreachable and the cards counted only what
 * was fetched (2026-10-29).
 */
const PAGE_SIZE = 50;

/** Human-readable byte size for attachment chips. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const STATUS_OPTIONS: { value: CorrectionRequestStatus; label: string }[] = [
  { value: "PENDING", label: "Pending" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
  { value: "NEEDS_CLARIFICATION", label: "Needs clarification" },
  { value: "CANCELLED", label: "Cancelled" },
];

const TYPE_OPTIONS: { value: CorrectionRequestType; label: string }[] = [
  { value: "MISSED_CHECK_IN", label: "Missed check-in" },
  { value: "MISSED_CHECK_OUT", label: "Missed check-out" },
  { value: "INCORRECT_LOCATION", label: "Incorrect location" },
  { value: "ATTENDANCE_CORRECTION", label: "Attendance correction" },
  { value: "LEAVE", label: "Leave" },
  { value: "OVERTIME", label: "Overtime" },
  { value: "SHIFT_SWAP", label: "Shift swap" },
  { value: "WORK_FROM_ANYWHERE", label: "Work from anywhere" },
  { value: "OTHER", label: "Other" },
];

/** "Approved by …" wording for a decided request (FEAT-2026-027). */
function decisionVerb(status: CorrectionRequestStatus): string {
  if (status === "APPROVED") return "Approved";
  if (status === "REJECTED") return "Rejected";
  return "Sent back";
}

const statusMeta: Record<CorrectionRequestStatus, { label: string; className: string }> = {
  PENDING: { label: "Pending", className: "bg-warning/10 text-warning border-warning/20" },
  APPROVED: { label: "Approved", className: "bg-success/10 text-success border-success/20" },
  REJECTED: { label: "Rejected", className: "bg-destructive/10 text-destructive border-destructive/20" },
  NEEDS_CLARIFICATION: { label: "Needs clarification", className: "bg-info/10 text-info border-info/20" },
  CANCELLED: { label: "Cancelled", className: "bg-muted text-muted-foreground border-border" },
};

/** Format `date` and optional `dateTo` as "Jan 3" or "Jan 3 – Jan 7", in tenant time. */
const formatDateRange = (from: string, to: string | null, tz: string): string => {
  if (!to || to === from) return formatDateIn(from, tz);
  return `${formatDateIn(from, tz)} – ${formatDateIn(to, tz)}`;
};

const Requests = () => {
  const tz = useTenantTimezone();
  const [list, setList] = useState<CorrectionRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<RequestSummary | null>(null);
  /** Bumped after a single decision, which updates its row in place, so the cards refetch. */
  const [countsKey, setCountsKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  const { search, setSearch, debouncedSearch } = useDebouncedSearch();
  const [statusFilter, setStatusFilter] = useState<"all" | CorrectionRequestStatus>("all");
  const [typeFilter, setTypeFilter] = useState<"all" | CorrectionRequestType>("all");
  const [assignedToMe, setAssignedToMe] = useState(false);

  const [active, setActive] = useState<CorrectionRequest | null>(null);
  const [notes, setNotes] = useState("");
  const [acting, setActing] = useState<null | "approve" | "reject" | "needs">(null);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  const filters = useMemo(() => ({
    search: debouncedSearch || undefined,
    status: statusFilter === "all" ? undefined : statusFilter,
    type: typeFilter === "all" ? undefined : typeFilter,
    assignedToMe: assignedToMe || undefined,
  }), [debouncedSearch, statusFilter, typeFilter, assignedToMe]);
  // Any filter change goes back to page 1.
  const [page, setPage] = usePage(JSON.stringify(filters));

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const q: ListRequestsQuery = { ...filters, page, pageSize: PAGE_SIZE };
    requestsApi
      .list(q)
      .then((r) => {
        if (cancelled) return;
        setList(r.data);
        setTotal(r.total);
      })
      .catch((e) => {
        if (cancelled) return;
        const msg = e instanceof ApiError ? e.message : "Failed to load requests";
        toast.error(msg);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filters, page, reloadKey]);

  // The cards count every request the filters match, not the rows on this page.
  useEffect(() => {
    let cancelled = false;
    requestsApi
      .summary(filters)
      .then((r) => { if (!cancelled) setSummary(r); })
      .catch(() => { if (!cancelled) setSummary(null); /* non-fatal — the cards show a dash */ });
    return () => { cancelled = true; };
  }, [filters, reloadKey, countsKey]);

  const triggerReload = () => setReloadKey((k) => k + 1);

  const pendingIds = useMemo(() => list.filter((r) => r.status === "PENDING").map((r) => r.id), [list]);
  const allPendingSelected = pendingIds.length > 0 && pendingIds.every((id) => selected.has(id));

  const togglePending = () => {
    if (allPendingSelected) setSelected(new Set());
    else setSelected(new Set(pendingIds));
  };

  const toggleOne = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const openDetail = (r: CorrectionRequest) => {
    setActive(r);
    setNotes(r.approvalNotes ?? "");
  };

  const [openingAtt, setOpeningAtt] = useState<string | null>(null);

  /**
   * Open an attachment. The chip's `url` is a stable API endpoint that
   * 302-redirects to a short-lived signed GET; we fetch it with the Clerk
   * bearer and hand the browser a local blob URL (FEAT-2026-008). A blank tab
   * is opened synchronously so the pop-up survives the await.
   */
  const openAttachment = async (att: RequestAttachment) => {
    setOpeningAtt(att.url);
    const tab = window.open("", "_blank");
    try {
      const objectUrl = await fetchSignedAsset(att.url);
      if (tab) tab.location.href = objectUrl;
      else window.open(objectUrl, "_blank");
      // Revoke a little later so the tab has loaded the bytes.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (e) {
      tab?.close();
      const msg = e instanceof ApiError ? e.message : "Could not open attachment";
      toast.error(msg);
    } finally {
      setOpeningAtt(null);
    }
  };

  const act = async (kind: "approve" | "reject" | "needs") => {
    if (!active) return;
    setActing(kind);
    try {
      const api = kind === "approve" ? requestsApi.approve
        : kind === "reject" ? requestsApi.reject
        : requestsApi.needsClarification;
      const updated = await api(active.id, { notes: notes || undefined });
      toast.success(
        kind === "approve" ? "Request approved"
          : kind === "reject" ? "Request rejected"
          : "Marked as needs clarification",
      );
      setList((prev) => prev.map((r) => r.id === updated.id ? updated : r));
      setActive(updated);
      setCountsKey((k) => k + 1);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : "Action failed";
      toast.error(msg);
    } finally {
      setActing(null);
    }
  };

  const bulkApprove = async () => {
    if (selected.size === 0) return;
    setBulkSubmitting(true);
    try {
      const result = await requestsApi.bulkApprove({ ids: Array.from(selected) });
      toast.success(`Approved ${result.approved.length} of ${result.totalProcessed}`, {
        description: result.skipped.length > 0 ? `${result.skipped.length} skipped` : undefined,
      });
      setSelected(new Set());
      triggerReload();
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : "Bulk approve failed";
      toast.error(msg);
    } finally {
      setBulkSubmitting(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* KPIs */}
      <div className="grid gap-3 sm:grid-cols-4">
        <KpiCard label="Pending" value={summary?.pending ?? null} tone="warning" />
        <KpiCard label="Approved" value={summary?.approved ?? null} tone="success" />
        <KpiCard label="Rejected" value={summary?.rejected ?? null} tone="destructive" />
        <KpiCard label="Needs clarification" value={summary?.needsClarification ?? null} tone="info" />
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[240px]">
            <Label className="text-xs text-muted-foreground">Search</Label>
            <div className="relative mt-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Request ID, employee name or code…" className="pl-9 h-9" />
            </div>
          </div>
          <div className="w-48">
            <Label className="text-xs text-muted-foreground">Status</Label>
            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
              <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-52">
            <Label className="text-xs text-muted-foreground">Type</Label>
            <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as typeof typeFilter)}>
              <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {TYPE_OPTIONS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 pb-1.5">
            <Switch id="assignedToMe" checked={assignedToMe} onCheckedChange={setAssignedToMe} />
            <Label htmlFor="assignedToMe" className="text-xs flex items-center gap-1.5 cursor-pointer">
              <UserCheck className="h-3.5 w-3.5" /> Assigned to me
            </Label>
          </div>
          {selected.size > 0 && (
            <Button size="sm" className="ml-auto" onClick={bulkApprove} disabled={bulkSubmitting}>
              {bulkSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <CheckCheck className="mr-1.5 h-4 w-4" />
              Approve {selected.size} selected
            </Button>
          )}
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
                <th className="p-3 text-left font-medium w-8">
                  <input
                    type="checkbox"
                    checked={allPendingSelected}
                    onChange={togglePending}
                    disabled={pendingIds.length === 0}
                  />
                </th>
                <th className="p-3 text-left font-medium">Request</th>
                <th className="p-3 text-left font-medium">Employee</th>
                <th className="p-3 text-left font-medium">Type</th>
                <th className="p-3 text-left font-medium">Date</th>
                <th className="p-3 text-left font-medium">Submitted</th>
                <th className="p-3 text-left font-medium">Status</th>
                <th className="p-3 text-left font-medium">Manager</th>
                <th className="p-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="p-12 text-center text-muted-foreground">
                  <Loader2 className="mx-auto h-6 w-6 animate-spin opacity-60" />
                </td></tr>
              ) : list.length === 0 ? (
                <tr><td colSpan={9} className="p-12 text-center text-muted-foreground">
                  <FileCheck2 className="mx-auto mb-2 h-8 w-8 opacity-40" />
                  No requests match your filters.
                </td></tr>
              ) : list.map((r) => {
                const meta = statusMeta[r.status];
                return (
                  <tr key={r.id} className="border-b last:border-0 hover:bg-secondary/30 cursor-pointer" onClick={() => openDetail(r)}>
                    <td className="p-3" onClick={(e) => e.stopPropagation()}>
                      {r.status === "PENDING" && (
                        <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleOne(r.id)} />
                      )}
                    </td>
                    <td className="p-3 font-mono text-xs text-muted-foreground">{r.id.slice(0, 10)}…</td>
                    <td className="p-3">
                      <div className="font-medium">{r.employeeName}</div>
                      <div className="text-[11px] text-muted-foreground">{r.employeeEmpCode}</div>
                    </td>
                    <td className="p-3">{displayTypeName(r.type, r.typeLabel, { admin: true })}</td>
                    <td className="p-3 text-muted-foreground">{formatDateRange(r.date, r.dateTo, tz)}</td>
                    <td className="p-3 text-muted-foreground text-xs">{formatDateTimeIn(r.createdAt, tz)}</td>
                    <td className="p-3">
                      <Badge variant="outline" className={`font-medium ${meta.className}`}>{meta.label}</Badge>
                      {r.decidedByName && (
                        <div className="mt-1 text-[11px] text-muted-foreground">
                          {`by ${r.decidedByName} (${r.decidedByRole === "LINE_MANAGER" ? "line manager" : "HR"})`}
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-muted-foreground">{r.managerName ?? "—"}</td>
                    <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" onClick={() => openDetail(r)}><Eye className="h-4 w-4" /></Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <ListPager page={page} pageSize={PAGE_SIZE} total={total} loading={loading} onPageChange={setPage} noun="requests" />
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="sm:max-w-2xl">
          {active && (<>
            <DialogHeader>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <DialogTitle className="flex items-center gap-2">
                    <span className="font-mono text-sm">{active.id}</span>
                    <Badge variant="outline" className={`font-medium ${statusMeta[active.status].className}`}>
                      {statusMeta[active.status].label}
                    </Badge>
                  </DialogTitle>
                  <DialogDescription className="mt-1">
                    Submitted {formatDateTimeIn(active.createdAt, tz)} · {displayTypeName(active.type, active.typeLabel, { admin: true })}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div className="grid grid-cols-3 gap-3">
                <InfoBlock icon={User} label="Employee" value={`${active.employeeName} · ${active.employeeEmpCode}`} />
                <InfoBlock
                  icon={Calendar}
                  label={active.dateTo && active.dateTo !== active.date ? "Dates" : "Date"}
                  value={formatDateRange(active.date, active.dateTo, tz)}
                />
                <InfoBlock icon={Clock} label="Manager" value={active.managerName ?? "— unassigned —"} />
              </div>

              {/* Structured detail when the app sent it; the prose `currentState`
                  is the fallback for older rows (and types that carry none). */}
              {hasStructuredDetail(active.metadata) ? (
                <RequestMetadataPanel metadata={active.metadata} tz={tz} />
              ) : (
                active.currentState && (
                  <div className="rounded-lg border bg-secondary/30 p-3">
                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Current state</div>
                    <div className="mt-1 text-sm">{active.currentState}</div>
                  </div>
                )
              )}

              <div className="rounded-lg border-l-4 border-primary bg-primary/5 p-3">
                <div className="text-xs font-semibold uppercase tracking-wider text-primary">Requested change</div>
                <div className="mt-1 text-sm font-medium">{active.requestedChange}</div>
              </div>

              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Reason from employee</div>
                <div className="rounded-lg border bg-background p-3 text-sm leading-relaxed">{active.reason}</div>
              </div>

              {active.attachments.length > 0 && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1.5">
                    <Paperclip className="h-3.5 w-3.5" />
                    Attachments ({active.attachments.length})
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {active.attachments.map((att) => {
                      const isImage = att.mimeType.startsWith("image/");
                      const Icon = isImage ? ImageIcon : FileText;
                      const busy = openingAtt === att.url;
                      return (
                        <button
                          key={att.url}
                          type="button"
                          onClick={() => openAttachment(att)}
                          disabled={busy}
                          title={att.filename}
                          className="inline-flex items-center gap-2 rounded-lg border bg-background px-3 py-2 text-sm hover:bg-secondary/40 transition-colors disabled:opacity-60"
                        >
                          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4 text-muted-foreground" />}
                          <span className="max-w-[12rem] truncate">{att.filename}</span>
                          <span className="text-xs text-muted-foreground">{formatBytes(att.bytes)}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {(active.approvedAt || active.decidedByName) && (
                <div className="rounded-lg border bg-muted/30 p-3 text-xs text-muted-foreground">
                  {active.decidedByName && (
                    <div className="mb-1 font-medium text-foreground">
                      {decisionVerb(active.status)} by {active.decidedByName}{" "}
                      ({active.decidedByRole === "LINE_MANAGER" ? "line manager" : "HR"})
                    </div>
                  )}
                  {active.approvedAt && <>Decided {formatDateTimeIn(active.approvedAt, tz)} · </>}
                  notes: {active.approvalNotes || "—"}
                </div>
              )}

              <div>
                <Label className="text-xs">Approval remarks</Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Add a note for the audit log…" className="mt-1.5" rows={2} />
              </div>
            </div>

            <DialogFooter className="gap-2">
              {active.status === "CANCELLED" ? (
                <div className="w-full text-xs text-muted-foreground italic">
                  This request was withdrawn by the employee and can no longer be decided.
                </div>
              ) : (
                <>
                  <Button variant="outline" onClick={() => act("needs")} disabled={acting !== null || active.status === "NEEDS_CLARIFICATION"}>
                    {acting === "needs" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <MessageSquare className="mr-1.5 h-4 w-4" />}
                    Needs clarification
                  </Button>
                  <Button variant="outline" className="text-destructive hover:text-destructive" onClick={() => act("reject")} disabled={acting !== null || active.status === "REJECTED"}>
                    {acting === "reject" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <X className="mr-1.5 h-4 w-4" />}
                    Reject
                  </Button>
                  <Button className="bg-gradient-success text-success-foreground" onClick={() => act("approve")} disabled={acting !== null || active.status === "APPROVED"}>
                    {acting === "approve" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Check className="mr-1.5 h-4 w-4" />}
                    Approve
                  </Button>
                </>
              )}
            </DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const KpiCard = ({ label, value, tone }: { label: string; value: number | null; tone: "warning" | "success" | "destructive" | "info" }) => {
  const toneClass =
    tone === "success" ? "text-success" :
    tone === "destructive" ? "text-destructive" :
    tone === "info" ? "text-info" :
    "text-warning";
  return (
    <Card className="p-4">
      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`mt-1 font-display text-2xl font-semibold ${toneClass}`}>{value === null ? "—" : value.toLocaleString()}</div>
    </Card>
  );
};

const InfoBlock = ({ icon: Icon, label, value }: { icon: React.ComponentType<{ className?: string }>; label: string; value: string }) => (
  <div className="rounded-lg border p-3">
    <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
      <Icon className="h-3 w-3" /> {label}
    </div>
    <div className="mt-1 text-sm font-medium truncate">{value}</div>
  </div>
);

export default Requests;
